import React, { useState, useMemo } from 'react';
import { Button, Form, Input, message, Row, TreeSelect, Popconfirm } from 'antd';
import { FormComponentProps } from 'antd/lib/form';
import I18N from 'src/services/I18NService';
import { TreeNode } from 'antd/lib/tree-select';
import TextArea from 'antd/lib/input/TextArea';
import { IDepartmentItem } from 'src/entity/DepartmentEntity';
import { IStateDepartment } from 'src/entity/StateEntity';
import { Dispatch } from 'redux';
import { createDepartment, deleteDepartment, updateDepartment } from 'src/redux/actions/departmentCreator';
import { isString } from 'lodash';
import DepartmentAdminService from 'src/services/DepartmentAdminService';

interface IProps extends FormComponentProps<{}> {
  onDispatch: Dispatch<IStateDepartment>,
  onFinished: () => void,
  onDeleted: () => void,
  allDepartments: IDepartmentItem[],
  departmentTree: TreeNode[],
  departmentId: string,
  formStatus: 'add' | 'edit',
}

const defaultDepartmentDetail: IDepartmentItem = {
  uuid: '',
  name: '',
  parent: null,
  description: '',
  has_staff: false,
}

const labelCol = {span: 5};

const DepartmentFormComp: React.FC<IProps> = (props) => {
  const {
    allDepartments,
    form,
    departmentId,
    departmentTree,
    formStatus,
    onFinished,
    onDispatch,
    onDeleted,
  } = props;
  const { getFieldDecorator } = form;

  const [loading, setLoading] = useState(false);
  const departmentDetail = useMemo(() => {
    let result: IDepartmentItem = { ...defaultDepartmentDetail };
    if (formStatus === 'add') {
      result.parent = departmentId;
    } else if (formStatus === 'edit') {
      const matchedItem = allDepartments.find(item => item.uuid === departmentId);
      if (matchedItem) {
        result = { ...matchedItem };
      }
    }
    return result;
  }, []);

  const handleSubmit = () => {
    form.validateFields(async (err, values: IDepartmentItem) => {
      if (!err) {
        const {name, parent, description} = values;
        const params: Partial<IDepartmentItem> = {
          name,
          parent,
          description,
        };
        setLoading(true);
        let res: IDepartmentItem | undefined;
        if (formStatus === "edit") {
          // 编辑部门
          params.uuid = departmentId;
          res = await onDispatch(updateDepartment(params));
        } else if (formStatus === "add") {
          // 新增部门
          res = await onDispatch(createDepartment(params));
        }
        setLoading(false);
        if (res) {
          message.success(I18N.get('common_submit_success'));
          handleClose();
        }
      }
    })
  };

  const handleClose = () => {
    onFinished();
  };

  const handleDelete = async () => {
    const hasDeleted = await onDispatch(deleteDepartment(departmentId));
    if (hasDeleted) {
      message.success('删除成功');
      handleClose();
      onDeleted();
    }
  };

  /**
   * 部门名称唯一性验证
   */
  const uniqueValidator = (rule: any, value: string, callback: any) => {
    if (isString(value)) {
      const isRepeat = allDepartments.findIndex(item => item.name.trim() === value.trim()) !== -1 && value !== departmentDetail.name;
      if (isRepeat) {
        callback('部门名称不可重复');
      }
    }
    callback();
  };

  /**
   * 验证父级部门是否为当前部门以及其嵌套子部门
   */
  const nestValidator = (rule: any, value: string, callback: any) => {
    // 只有编辑时才验证是否嵌套
    if (isString(value) && formStatus === 'edit') {
      const currentId = departmentDetail.uuid;
      const childDepartments = DepartmentAdminService.getChildDepartments(currentId);
      const childIds = childDepartments.map(item => item.uuid);
      const validateIds = [currentId, ...childIds];
      const isNested = validateIds.findIndex(item => item === value) !== -1;
      if (isNested) {
        callback('父部门不能为当前部门以及其下子部门');
      }
    }
    callback();
  }


  return (
    <Form className="mdd-search-form">
      <Form.Item
        label={I18N.get('departmentAdmin_department_name')}
        labelCol={labelCol}
      >
        {getFieldDecorator('name', {
          initialValue: departmentDetail.name,
          rules: [
            {
              required: true,
              message: `${I18N.get('common_input')}${I18N.get('departmentAdmin_department_name')}`
            },
            {
              max: 5,
              message: '请限制在5字内'
            },
            {
              validator: uniqueValidator,
            }
          ]
        })(
          <Input
            autoComplete="off"
            placeholder={`${I18N.get('common_input')}${I18N.get('departmentAdmin_department_name')}`}
          />
        )}
      </Form.Item>
      <Form.Item
        label={I18N.get('departmentAdmin_department_parent')}
        labelCol={labelCol}
      >
        {getFieldDecorator('parent', {
          initialValue: departmentDetail.parent,
          rules: [
            {
              required: true,
              message: `${I18N.get('common_choose')}${I18N.get('departmentAdmin_department_parent')}`
            },
            {
              validator: nestValidator,
            }
          ]
        })(
          <TreeSelect
            style={{ width: '100%' }}
            dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
            allowClear={true}
            treeData={departmentTree}
            treeDefaultExpandAll={true}
            placeholder={`${I18N.get('common_choose')}${I18N.get('departmentAdmin_department_parent')}`}
          />
        )}
      </Form.Item>
      <Form.Item
        label={I18N.get('departmentAdmin_department_desc')}
        style={{marginBottom: 0}}
        labelCol={labelCol}
      >
        {getFieldDecorator('description', {
          initialValue: departmentDetail.description,
          rules: [
            {
              max: 200,
              message: `${I18N.get('setting_announcement_error')}`
            }
          ]
        })(
          <TextArea
            autoComplete="off"
            style={{height: 100}}
            maxLength={200}
            placeholder={`${I18N.get('common_input')}${I18N.get('departmentAdmin_department_desc')}, ${I18N.get('setting_announcement_error')}`}
          />
        )}
      </Form.Item>
      <Row type="flex" justify="center" style={{marginTop: 30}}>
        <Button
          type="primary"
          onClick={handleSubmit}
          loading={loading}
        >{I18N.get('common_ok')}</Button>
        <Button
          onClick={handleClose}
          style={{marginLeft: 16}}
        >{I18N.get('common_cancel')}</Button>
        {formStatus === 'edit' && (
          <Popconfirm
            title="是否确认删除此部门以及其下子部门?"
            onConfirm={handleDelete}
            okText={I18N.get('common_ok')}
            cancelText={I18N.get('common_cancel')}
            disabled={loading}
          >
            <Button
              type="danger"
              loading={loading}
              style={{marginLeft: 16}}
            >{I18N.get('common_delete')}</Button>
          </Popconfirm>
        )}
      </Row>
    </Form>
  )
}

const WrappedDepartmentForm = Form.create<IProps>()(DepartmentFormComp);
const DepartmentForm =  React.memo(WrappedDepartmentForm);
export default DepartmentForm;


// WEBPACK FOOTER //
// ./src/components/views/DepartmentAdmin/Department/DepartmentForm.tsx